/* api.services is untyped JS (allowJs); apiClient wraps axios and returns the response envelope. */
import { apiClient, apiEndpoint } from '../../../../../../services/api.services';
import type { BulkSaveProduct, GetMeasurementsResult, SupportingDocsByProduct, ValidateGradingResult } from './types';

/** Shape of the standard `{ code, message, data }` envelope returned by apiClient. */
interface ApiEnvelope<TData> {
  code?: number;
  message?: string;
  data?: TData;
}

const BASE = `${apiEndpoint}/order-measurements/bulk`;

/** Validate that the selected products share a common grading group. */
export async function validateGrading(devOrderIds: string[]): Promise<ValidateGradingResult> {
  const res = (await apiClient.post(`${BASE}/validate-grading`, { devOrderIds })) as ApiEnvelope<ValidateGradingResult>;
  if (res?.code !== 200 || !res.data) throw new Error(res?.message ?? 'Grading validation failed.');
  return res.data;
}

/**
 * Fetch measurement (POM/spec) data for one page of products under one grading group.
 * Send the full cohort in `devOrderIds`; the server slices to `pageIndex`/`pageSize` when both are given.
 */
export async function getMeasurements(
  devOrderIds: string[],
  gradingGroupId: string,
  pageIndex?: number,
  pageSize?: number,
): Promise<GetMeasurementsResult> {
  const body: Record<string, unknown> = { devOrderIds, gradingGroupId };
  if (pageIndex !== undefined && pageSize !== undefined) {
    body.pageIndex = pageIndex;
    body.pageSize = pageSize;
  }
  const res = (await apiClient.post(`${BASE}/measurements`, body)) as ApiEnvelope<GetMeasurementsResult>;
  if (res?.code !== 200 || !res.data) throw new Error(res?.message ?? 'Failed to load measurements.');
  return res.data;
}

/** Persist edited measurement values for many products at once. */
export async function saveMeasurements(gradingGroupId: string, products: BulkSaveProduct[]): Promise<{ savedCount: number }> {
  const res = (await apiClient.put(`${BASE}/measurements`, { gradingGroupId, products })) as ApiEnvelope<{ savedCount: number }>;
  if (res?.code !== 200 || !res.data) throw new Error(res?.message ?? 'Failed to save measurements.');
  return res.data;
}

/** Clear highlight (change) flags for many products under one grading group. */
export async function clearHighlights(devOrderIds: string[], gradingGroupId: string): Promise<{ clearedCount: number }> {
  const res = (await apiClient.put(`${BASE}/clear-highlights`, { devOrderIds, gradingGroupId })) as ApiEnvelope<{ clearedCount: number }>;
  if (res?.code !== 200 || !res.data) throw new Error(res?.message ?? 'Failed to clear highlights.');
  return res.data;
}

/**
 * Fetch supporting (specification) documents per product for the grid's document columns.
 * Send the full cohort in `devOrderIds`; the server slices to `pageIndex`/`pageSize` when both are given.
 */
export async function getSupportingDocs(
  devOrderIds: string[],
  gradingGroupId: string,
  pageIndex?: number,
  pageSize?: number,
): Promise<SupportingDocsByProduct> {
  const body: Record<string, unknown> = { devOrderIds, gradingGroupId };
  if (pageIndex !== undefined && pageSize !== undefined) {
    body.pageIndex = pageIndex;
    body.pageSize = pageSize;
  }
  const res = (await apiClient.post(`${BASE}/documents`, body)) as ApiEnvelope<SupportingDocsByProduct>;
  if (res?.code !== 200 || !res.data) throw new Error(res?.message ?? 'Failed to load supporting documents.');
  return res.data;
}

/** Upload one supporting document for a single product under the common grading group. */
export async function uploadDocument(formData: FormData): Promise<{ _id: string }> {
  const res = (await apiClient.post(`${BASE}/documents/upload`, formData)) as ApiEnvelope<{ _id: string }>;
  if (res?.code !== 200 || !res.data) throw new Error(res?.message ?? 'Failed to upload document.');
  return res.data;
}

/** Delete one supporting document (S3 object + record) — reuses the shared order-measurements delete endpoint. */
export async function deleteDocument(documentId: string, s3Key: string): Promise<void> {
  const res = (await apiClient.delete(`${apiEndpoint}/order-measurements/delete`, {
    data: { deletedData: [{ _id: documentId, key: s3Key }] },
  })) as ApiEnvelope<unknown>;
  if (res?.code !== 200) throw new Error(res?.message ?? 'Failed to delete document.');
}
